import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { profile } from "../data/content";
import { logout } from "../lib/auth";

const links = [
  { id: "about", label: "About" },
  { id: "projects", label: "Projects" },
  { id: "experience", label: "Experience" },
  { id: "skills", label: "Skills" },
  { id: "contact", label: "Contact" },
];

export default function Navbar({ onNavigate }) {
  const [active, setActive] = useState("");
  const [open, setOpen] = useState(false);

  // Track which section is in view to drive the active underline.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    links.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    onNavigate(id);
  };

  return (
    <header className="sticky top-0 z-50 bg-canvas/95 border-b border-hairline" data-testid="navbar">
      <div className="container-x flex items-center justify-between h-16">
        <button
          data-testid="nav-logo"
          onClick={() => go("top")}
          className="w-9 h-9 border border-cardline bg-panel flex items-center justify-center font-display text-sm font-semibold tracking-wide text-ink hover:border-teal hover:text-teal transition-colors duration-300"
          aria-label="Back to top"
        >
          {profile.initials}
        </button>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {links.map(({ id, label }) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => go(id)}
              className={`relative text-sm transition-colors duration-300 pb-1 ${
                active === id ? "text-ink" : "text-faint hover:text-ink"
              }`}
            >
              {label}
              <span
                className={`absolute left-0 -bottom-0.5 h-[2px] bg-teal transition-all duration-300 ${
                  active === id ? "w-full" : "w-0"
                }`}
                aria-hidden="true"
              />
            </button>
          ))}
          <button
            data-testid="nav-signout-button"
            onClick={async () => {
              await logout();
              window.location.reload();
            }}
            className="text-sm text-faint hover:text-teal transition-colors duration-300"
          >
            Sign out
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-ink"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <nav className="md:hidden border-t border-hairline bg-canvas" aria-label="Mobile">
          {links.map(({ id, label }) => (
            <button
              key={id}
              data-testid={`nav-mobile-link-${id}`}
              onClick={() => go(id)}
              className="block w-full text-left px-6 py-4 text-sm text-body border-b border-hairline last:border-0 hover:text-teal transition-colors"
            >
              {label}
            </button>
          ))}
          <button
            data-testid="nav-mobile-signout-button"
            onClick={async () => {
              await logout();
              window.location.reload();
            }}
            className="block w-full text-left px-6 py-4 text-sm text-faint hover:text-teal transition-colors"
          >
            Sign out
          </button>
        </nav>
      )}
    </header>
  );
}
